import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Phone, User, Bot, Shield, RefreshCw, Send, AlertCircle, CheckCircle, ChevronLeft, Search, Archive, ArchiveRestore, Trash2, X, Clock, History, Globe, Smartphone, Tag, UserCheck, MapPin, Mail, Package, Info, Users, List } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { supabase } from '../../lib/supabase';

// ─── Helpers ────────────────────────────────────────────────────────────────
function cleanPhone(phone) {
  return String(phone || '')?.replace(/\D/g, '');
}

function displayPhone(phone) {
  if (!phone) return '';
  const digits = cleanPhone(phone);
  if (digits?.startsWith('33') && digits?.length === 11) {
    return '+33 ' + digits?.substring(2)?.replace(/(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  }
  return '+' + digits;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return d?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatSessionDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function normalizeText(str) {
  return String(str || '')?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '');
}

function shortSession(sessionId) {
  if (!sessionId) return '????????';
  return String(sessionId)?.replace(/-/g, '')?.substring(0, 8)?.toUpperCase();
}

function shortVisitorId(visitorId) {
  if (!visitorId) return '—';
  return String(visitorId)?.replace(/-/g, '')?.substring(0, 8)?.toUpperCase();
}

function getConversationStatus(rows) {
  const archivedRows = rows?.filter(r => r?.archived === true);
  if (archivedRows?.length === 0) return 'open';
  const latestArchivedAt = archivedRows?.map(r => r?.archived_at)?.filter(Boolean)?.sort()?.pop();
  if (!latestArchivedAt) return 'archived';
  const hasNewerMessage = rows?.some(r => r?.created_at && new Date(r.created_at) > new Date(latestArchivedAt));
  if (hasNewerMessage) return 'reopened';
  return 'archived';
}

const LEAD_STATUS_LABELS = {
  new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-700' },
  qualified: { label: 'Qualifié', color: 'bg-green-100 text-green-700' },
  needs_callback: { label: 'À rappeler', color: 'bg-orange-100 text-orange-700' },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  converted: { label: 'Converti', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Fermé', color: 'bg-red-100 text-red-700' },
  archived: { label: 'Archivé', color: 'bg-gray-100 text-gray-500' },
};

const INTENT_LABELS = {
  barrique_offer: 'Barrique Offre Été 2026',
  carton_quote: 'Devis carton',
  tv_quote: 'Devis téléviseur',
  tracking: 'Suivi colis',
  callback_request: 'Demande de rappel',
  out_of_idf: 'Hors Île-de-France',
  out_of_destination: 'Hors destination',
  complaint: 'Réclamation',
  human_help: 'Aide humaine',
};

// ─── Toast ───────────────────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = { success: 'bg-green-600', error: 'bg-red-600', info: 'bg-blue-600' };

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${colors?.[type] || colors?.success}`}>
      {type === 'success' && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
      {type === 'error' && <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      {type === 'info' && <Clock className="h-4 w-4 flex-shrink-0" />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-1 opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// ─── Confirm Dialog ──────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Confirmation requise</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">Supprimer définitivement</button>
        </div>
      </div>
    </div>
  );
}

// ─── WhatsApp Message Bubble ─────────────────────────────────────────────────
function WaBubble({ msg }) {
  const bubbles = [];
  if (msg?.user_message) {
    bubbles?.push(
      <div key="user" className="flex justify-start mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1">
            <User className="h-3 w-3 text-gray-500" />
            <span className="text-xs text-gray-500">Client</span>
          </div>
          <div className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{msg?.user_message}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 ml-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }
  if (msg?.assistant_response) {
    bubbles?.push(
      <div key="assistant" className="flex justify-end mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1 justify-end">
            <span className="text-xs text-emerald-600">Fatou IA</span>
            <Bot className="h-3 w-3 text-emerald-600" />
          </div>
          <div className="bg-emerald-500 rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-white whitespace-pre-wrap">{msg?.assistant_response}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 text-right mr-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }
  if (msg?.admin_message) {
    bubbles?.push(
      <div key="admin" className="flex justify-end mb-2">
        <div className="max-w-[75%]">
          <div className="flex items-center gap-1 mb-1 justify-end">
            <span className="text-xs text-blue-600">Admin</span>
            <Shield className="h-3 w-3 text-blue-600" />
          </div>
          <div className="bg-blue-500 rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
            <p className="text-sm text-white whitespace-pre-wrap">{msg?.admin_message}</p>
          </div>
          <p className="text-xs text-gray-400 mt-1 text-right mr-1">{formatTime(msg?.created_at)}</p>
        </div>
      </div>
    );
  }
  if (bubbles?.length === 0) return null;
  return <>{bubbles}</>;
}

// ─── Web Message Bubble ──────────────────────────────────────────────────────
function WebBubble({ msg }) {
  return (
    <div className="mb-4">
      {msg?.user_message && (
        <div className="flex justify-start mb-2">
          <div className="max-w-[75%]">
            <div className="flex items-center gap-1 mb-1">
              <Globe className="h-3 w-3 text-indigo-500" />
              <span className="text-xs text-indigo-500">Visiteur site web</span>
            </div>
            <div className="bg-white dark:bg-slate-700 border border-indigo-200 dark:border-indigo-700 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{msg?.user_message}</p>
            </div>
            <p className="text-xs text-gray-400 mt-1 ml-1">{formatTime(msg?.created_at)}</p>
          </div>
        </div>
      )}
      {msg?.ai_response && (
        <div className="flex justify-end mb-2">
          <div className="max-w-[75%]">
            <div className="flex items-center gap-1 mb-1 justify-end">
              <span className="text-xs text-emerald-600">Fatou IA</span>
              <Bot className="h-3 w-3 text-emerald-600" />
            </div>
            <div className="bg-emerald-500 rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm">
              <p className="text-sm text-white whitespace-pre-wrap">{msg?.ai_response}</p>
            </div>
            <div className="flex items-center justify-end gap-2 mt-1 mr-1">
              <p className="text-xs text-gray-400">{formatTime(msg?.created_at)}</p>
              {msg?.confidence_score != null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  msg?.confidence_score >= 0.8 ? 'bg-green-100 text-green-700' :
                  msg?.confidence_score >= 0.5 ? 'bg-yellow-100 text-yellow-700': 'bg-red-100 text-red-700'
                }`}>
                  {Math.round(msg?.confidence_score * 100)}%
                </span>
              )}
              {msg?.is_flagged && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">⚑ Signalé</span>
              )}
            </div>
          </div>
        </div>
      )}
      {msg?.conversation_topic && (
        <div className="flex justify-center mb-1">
          <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
            <Tag className="h-2.5 w-2.5" />
            {msg?.conversation_topic}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Session Separator ───────────────────────────────────────────────────────
function SessionSeparator({ sessionId, firstMessageDate, messageCount, index }) {
  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-indigo-200 dark:bg-indigo-800"></div>
      <div className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-full">
        <Clock className="h-3 w-3 text-indigo-500" />
        <span className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
          Session du {formatSessionDate(firstMessageDate)}
        </span>
        <span className="text-[10px] text-indigo-400">· {messageCount} msg</span>
      </div>
      <div className="flex-1 h-px bg-indigo-200 dark:bg-indigo-800"></div>
    </div>
  );
}

// ─── Compute fresh summary from consolidated visitor data ────────────────────
function buildComputedSummary(visitor) {
  if (!visitor) return null;

  const parts = [];

  // Product / intent
  if (visitor?.last_product) {
    const intentLabel = INTENT_LABELS?.[visitor?.last_intent];
    if (intentLabel && intentLabel !== visitor?.last_product) {
      parts?.push(`Demande : ${visitor?.last_product} — ${intentLabel}`);
    } else {
      parts?.push(`Demande : ${visitor?.last_product}`);
    }
  } else if (visitor?.last_intent) {
    const intentLabel = INTENT_LABELS?.[visitor?.last_intent];
    if (intentLabel) parts?.push(`Demande : ${intentLabel}`);
  }

  // Name — never "Nom non fourni" if name is known
  if (visitor?.name) {
    parts?.push(`Nom : ${visitor?.name}`);
  }

  // Phone — use the visitor's current phone, not an old session phone
  if (visitor?.phone) {
    parts?.push(`Téléphone : ${visitor?.phone}`);
  }

  // City / postal code
  if (visitor?.city && visitor?.postal_code) {
    parts?.push(`Ville : ${visitor?.city}. Code postal : ${visitor?.postal_code}`);
  } else if (visitor?.city) {
    parts?.push(`Ville : ${visitor?.city}`);
  } else if (visitor?.postal_code) {
    parts?.push(`Code postal : ${visitor?.postal_code}`);
  }

  // Status
  if (visitor?.status && visitor?.status !== 'new') {
    const statusLabel = LEAD_STATUS_LABELS?.[visitor?.status]?.label;
    if (statusLabel) parts?.push(`Statut : ${statusLabel?.toLowerCase()}`);
  }

  if (parts?.length === 0) {
    // Fall back to stored summary only if we have nothing better
    return visitor?.last_summary || null;
  }

  return parts?.join('. ') + '.';
}

// ─── Visitor Card ─────────────────────────────────────────────────────────────
function VisitorCard({ visitor, onStatusChange, onToggleTakeover, onArchive, loading }) {
  if (!visitor) return null;

  const statusInfo = LEAD_STATUS_LABELS?.[visitor?.status] || LEAD_STATUS_LABELS?.new;
  const intentLabel = INTENT_LABELS?.[visitor?.last_intent];

  // Use computed summary (from consolidated visitor data) instead of raw last_summary
  const displaySummary = buildComputedSummary(visitor);

  return (
    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">Fiche visiteur</span>
        </div>
        <span className="text-[10px] font-mono text-gray-400">{shortVisitorId(visitor?.visitor_id)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {visitor?.name && (
          <div className="flex items-center gap-1.5">
            <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{visitor?.name}</span>
          </div>
        )}
        {visitor?.phone && (
          <div className="flex items-center gap-1.5">
            <Phone className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{visitor?.phone}</span>
          </div>
        )}
        {visitor?.email && (
          <div className="flex items-center gap-1.5 col-span-2">
            <Mail className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{visitor?.email}</span>
          </div>
        )}
        {(visitor?.city || visitor?.postal_code) && (
          <div className="flex items-center gap-1.5 col-span-2">
            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
              {[visitor?.postal_code, visitor?.city]?.filter(Boolean)?.join(' ')}
            </span>
          </div>
        )}
        {visitor?.last_product && (
          <div className="flex items-center gap-1.5 col-span-2">
            <Package className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{visitor?.last_product}</span>
          </div>
        )}
        {intentLabel && (
          <div className="flex items-center gap-1.5 col-span-2">
            <Tag className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{intentLabel}</span>
          </div>
        )}
      </div>
      {/* Status badge */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${statusInfo?.color}`}>
          {statusInfo?.label}
        </span>
        {visitor?.human_takeover && (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">
            <Shield className="h-2.5 w-2.5" />Reprise humaine
          </span>
        )}
        {visitor?.status === 'needs_callback' && (
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700">
            <Phone className="h-2.5 w-2.5" />À rappeler
          </span>
        )}
        <span className="text-[10px] text-gray-400 ml-auto">
          Vu {formatDate(visitor?.last_seen_at)}
        </span>
      </div>
      {/* Summary */}
      {displaySummary && (
        <div className="bg-white dark:bg-slate-800 rounded-lg p-2.5 mb-3 border border-indigo-100 dark:border-indigo-800">
          <div className="flex items-center gap-1 mb-1">
            <Info className="h-3 w-3 text-indigo-500" />
            <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400">Résumé</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{displaySummary}</p>
        </div>
      )}
      {/* Admin actions */}
      <div className="flex flex-wrap gap-1.5">
        <select
          value={visitor?.status || 'new'}
          onChange={e => onStatusChange(visitor?.id, e?.target?.value)}
          disabled={loading}
          className="flex-1 min-w-0 text-xs px-2 py-1.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
        >
          {Object.entries(LEAD_STATUS_LABELS)?.map(([key, val]) => (
            <option key={key} value={key}>{val?.label}</option>
          ))}
        </select>
        <button
          onClick={() => onToggleTakeover(visitor?.id, !visitor?.human_takeover)}
          disabled={loading}
          title={visitor?.human_takeover ? 'Désactiver reprise humaine' : 'Activer reprise humaine'}
          className={`flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded-lg transition-colors disabled:opacity-50 ${
            visitor?.human_takeover
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' :'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
        >
          <Shield className="h-3 w-3" />
          {visitor?.human_takeover ? 'Réactiver IA' : 'Reprise humaine'}
        </button>
        <button
          onClick={() => onArchive(visitor?.id)}
          disabled={loading || visitor?.is_archived}
          title="Archiver le visiteur"
          className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
        >
          <Archive className="h-3 w-3" />
          Archiver
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function AdminFatouConversations() {
  const [sourceTab, setSourceTab] = useState('whatsapp');
  // 'visitor' = grouped by visitor_id (default), 'session' = one row per session
  const [webViewMode, setWebViewMode] = useState('visitor');

  // WhatsApp state
  const [allWaMessages, setAllWaMessages] = useState([]);
  const [waConversations, setWaConversations] = useState([]);
  const [loadingWa, setLoadingWa] = useState(true);
  const [waError, setWaError] = useState(null);

  // Web state
  const [webLogs, setWebLogs] = useState([]);
  const [webConversations, setWebConversations] = useState([]); // per-session
  const [webVisitorGroups, setWebVisitorGroups] = useState([]); // per-visitor
  const [loadingWeb, setLoadingWeb] = useState(true);
  const [webError, setWebError] = useState(null);

  // Visitor state
  const [visitorsMap, setVisitorsMap] = useState({}); // keyed by visitor_id
  const [loadingVisitor, setLoadingVisitor] = useState(false);

  // Selection
  // For visitor mode: selectedId = visitor_id, selectedSource = 'web-visitor'
  // For session mode: selectedId = session_id, selectedSource = 'web'
  // For WA: selectedId = phone, selectedSource = 'whatsapp'
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  // UI
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileShowMessages, setMobileShowMessages] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // WA reply
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const messagesEndRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  // ── Build WA conversations ──────────────────────────────────────────────
  const buildWaConversations = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      const phone = String(row?.phone || '')?.replace(/\D/g, '');
      if (!phone) continue;
      if (!grouped?.[phone]) grouped[phone] = { phone, rows: [] };
      grouped?.[phone]?.rows?.push(row);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || latest?.assistant_response || latest?.admin_message || 'Message vide';
      const convStatus = getConversationStatus(g?.rows);
      const archivedRows = g?.rows?.filter(r => r?.archived === true && r?.archived_at);
      const lastArchivedAt = archivedRows?.length > 0
        ? archivedRows?.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at))?.[0]?.archived_at
        : null;

      return {
        source: 'whatsapp',
        id: g?.phone,
        phone: g?.phone,
        displayName: displayPhone(g?.phone),
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: convStatus,
        lastArchivedAt,
        messages: g?.rows,
        human_takeover: false,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Build Web conversations (per session) ───────────────────────────────
  const buildWebConversations = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      const sid = row?.session_id;
      if (!sid) continue;
      if (!grouped?.[sid]) grouped[sid] = { session_id: sid, rows: [] };
      grouped?.[sid]?.rows?.push(row);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || 'Message vide';
      const hasFlagged = g?.rows?.some(r => r?.is_flagged);
      const visitorId = latest?.visitor_id || null;
      const leadStatus = latest?.lead_status || null;
      const humanTakeover = g?.rows?.some(r => r?.human_takeover);

      return {
        source: 'web',
        id: g?.session_id,
        session_id: g?.session_id,
        visitor_id: visitorId,
        displayName: `Visiteur site web`,
        sessionShort: shortSession(g?.session_id),
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: 'open',
        lead_status: leadStatus,
        is_flagged: hasFlagged,
        human_takeover: humanTakeover,
        messages: g?.rows,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Build Web visitor groups (per visitor_id) ───────────────────────────
  const buildWebVisitorGroups = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      // Use visitor_id if available, fallback to visitor_ref_id, then session_id
      const vid = row?.visitor_id || row?.visitor_ref_id || row?.session_id;
      if (!vid) continue;
      if (!grouped?.[vid]) grouped[vid] = { visitor_id: vid, rows: [], sessions: new Set() };
      grouped?.[vid]?.rows?.push(row);
      if (row?.session_id) grouped?.[vid]?.sessions?.add(row?.session_id);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || 'Message vide';
      const hasFlagged = g?.rows?.some(r => r?.is_flagged);
      const humanTakeover = g?.rows?.some(r => r?.human_takeover);
      const leadStatus = latest?.lead_status || null;
      const sessionCount = g?.sessions?.size;

      return {
        source: 'web-visitor',
        id: g?.visitor_id,
        visitor_id: g?.visitor_id,
        session_ids: Array.from(g?.sessions),
        session_count: sessionCount,
        displayName: `Visiteur site web`,
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: 'open',
        lead_status: leadStatus,
        is_flagged: hasFlagged,
        human_takeover: humanTakeover,
        messages: g?.rows,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Load WhatsApp messages ──────────────────────────────────────────────
  const loadWaMessages = useCallback(async () => {
    setLoadingWa(true);
    setWaError(null);
    try {
      const { data, error } = await supabase?.from('wa_messages')?.select('id, phone, user_message, assistant_response, admin_message, created_at, order_confirmed, archived, archived_at, archived_by')?.order('created_at', { ascending: false })?.limit(1000);

      if (error) { setWaError(error?.message); setLoadingWa(false); return; }

      const convList = buildWaConversations(data);

      if (convList?.length > 0) {
        const phones = convList?.map(c => c?.phone);
        const { data: convData } = await supabase?.from('wa_conversations')?.select('phone, human_takeover')?.in('phone', phones);
        if (convData?.length > 0) {
          const convMap = {};
          for (const c of convData) convMap[String(c.phone || '').replace(/\D/g, '')] = c;
          for (const g of convList) {
            const match = convMap?.[g?.phone];
            if (match) g.human_takeover = match?.human_takeover ?? false;
          }
        }
      }

      setAllWaMessages(data || []);
      setWaConversations(convList);
    } catch (err) {
      setWaError(err?.message || 'Erreur inattendue');
    } finally {
      setLoadingWa(false);
    }
  }, [buildWaConversations]);

  // ── Load Web logs ───────────────────────────────────────────────────────
  const loadWebLogs = useCallback(async () => {
    setLoadingWeb(true);
    setWebError(null);
    try {
      const { data, error } = await supabase?.from('fatou_conversation_logs')
        ?.select('id, session_id, visitor_id, visitor_ref_id, user_message, ai_response, confidence_score, is_flagged, flag_reason, conversation_topic, response_time_ms, lead_status, human_takeover, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(1000);

      if (error) { setWebError(error?.message); setLoadingWeb(false); return; }

      setWebLogs(data || []);
      setWebConversations(buildWebConversations(data));
      setWebVisitorGroups(buildWebVisitorGroups(data));
    } catch (err) {
      setWebError(err?.message || 'Erreur inattendue');
    } finally {
      setLoadingWeb(false);
    }
  }, [buildWebConversations, buildWebVisitorGroups]);

  // ── Load visitor for a web conversation ────────────────────────────────
  const loadVisitorForConv = useCallback(async (visitorId) => {
    if (!visitorId || visitorsMap?.[visitorId]) return;
    setLoadingVisitor(true);
    try {
      const { data } = await supabase?.from('fatou_web_visitors')
        ?.select('*')
        ?.eq('visitor_id', visitorId)
        ?.maybeSingle();
      if (data) {
        setVisitorsMap(prev => ({ ...prev, [visitorId]: data }));
      }
    } catch (err) {
      console.error('loadVisitorForConv error:', err);
    } finally {
      setLoadingVisitor(false);
    }
  }, [visitorsMap]);

  useEffect(() => {
    loadWaMessages();
    loadWebLogs();
  }, [loadWaMessages, loadWebLogs]);

  // ── Real-time WA ────────────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase?.channel('fatou_conv_wa_rt')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'wa_messages' }, (payload) => {
        const newMsg = payload?.new;
        setAllWaMessages(prev => {
          const updated = [newMsg, ...prev];
          setWaConversations(buildWaConversations(updated));
          return updated;
        });
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [buildWaConversations]);

  // ── Real-time Web ───────────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase?.channel('fatou_conv_web_rt')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'fatou_conversation_logs' }, (payload) => {
        const newLog = payload?.new;
        setWebLogs(prev => {
          const updated = [newLog, ...prev];
          setWebConversations(buildWebConversations(updated));
          setWebVisitorGroups(buildWebVisitorGroups(updated));
          return updated;
        });
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [buildWebConversations, buildWebVisitorGroups]);

  // ── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  });

  // ── Load visitor when web conv selected ────────────────────────────────
  useEffect(() => {
    if (selectedSource === 'web' && selectedId) {
      const conv = webConversations?.find(c => c?.id === selectedId);
      if (conv?.visitor_id) {
        loadVisitorForConv(conv?.visitor_id);
      }
    }
    if (selectedSource === 'web-visitor' && selectedId) {
      loadVisitorForConv(selectedId);
    }
  }, [selectedId, selectedSource, webConversations, loadVisitorForConv]);

  // ── Compute unified conversation list ───────────────────────────────────
  const allConversations = [
    ...waConversations?.map(c => ({ ...c, source: 'whatsapp' })),
    ...(webViewMode === 'visitor' ? webVisitorGroups : webConversations)?.map(c => ({ ...c })),
  ]?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));

  const getConversationList = () => {
    if (sourceTab === 'whatsapp') return waConversations;
    if (sourceTab === 'web') return webViewMode === 'visitor' ? webVisitorGroups : webConversations;
    return allConversations;
  };

  const filteredConversations = getConversationList()?.filter(conv => {
    if (conv?.source === 'whatsapp') {
      if (statusFilter === 'open' && conv?.status !== 'open' && conv?.status !== 'reopened') return false;
      if (statusFilter === 'archived' && conv?.status !== 'archived') return false;
    } else {
      if (statusFilter === 'archived') return false;
    }

    if (!searchQuery) return true;

    const search = normalizeText(searchQuery);
    const cleanSearchPhone = searchQuery?.replace(/\D/g, '');

    // Also search in visitor data
    const visitorId = conv?.visitor_id;
    const visitor = visitorId ? visitorsMap?.[visitorId] : null;
    if (visitor) {
      const visitorText = [visitor?.name, visitor?.phone, visitor?.email, visitor?.city, visitor?.postal_code, visitor?.last_summary, visitor?.last_intent, visitor?.status]?.filter(Boolean)?.join(' ');
      if (normalizeText(visitorText)?.includes(search)) return true;
      if (cleanSearchPhone && visitor?.phone?.replace(/\D/g, '')?.includes(cleanSearchPhone)) return true;
    }

    return conv?.messages?.some(row => {
      if (conv?.source === 'whatsapp') {
        const phone = String(row?.phone || '');
        const cleanRowPhone = phone?.replace(/\D/g, '');
        const text = [row?.phone, row?.user_message, row?.assistant_response, row?.admin_message, row?.created_at]?.filter(Boolean)?.join(' ');
        const normalizedText = normalizeText(text);
        return normalizedText?.includes(search) || (cleanSearchPhone && cleanRowPhone?.includes(cleanSearchPhone));
      } else {
        const text = [row?.session_id, row?.visitor_id, row?.user_message, row?.ai_response, row?.conversation_topic, row?.lead_status, row?.created_at]?.filter(Boolean)?.join(' ');
        const normalizedText = normalizeText(text);
        return normalizedText?.includes(search) || (cleanSearchPhone && String(row?.session_id || '')?.includes(cleanSearchPhone));
      }
    });
  });

  // ── Selected conversation messages ──────────────────────────────────────
  const selectedMessages = (() => {
    if (!selectedId || !selectedSource) return [];
    if (selectedSource === 'whatsapp') {
      return [...allWaMessages?.filter(row => String(row?.phone || '')?.replace(/\D/g, '') === selectedId)]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (selectedSource === 'web-visitor') {
      // All messages for this visitor across all sessions, sorted ASC
      return [...webLogs?.filter(row => {
        const vid = row?.visitor_id || row?.visitor_ref_id || row?.session_id;
        return vid === selectedId;
      })]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else {
      return [...webLogs?.filter(row => row?.session_id === selectedId)]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
  })();

  // ── Group selected messages by session (for visitor view) ───────────────
  const selectedMessagesBySession = (() => {
    if (selectedSource !== 'web-visitor') return null;
    const sessionMap = {};
    for (const msg of selectedMessages) {
      const sid = msg?.session_id || 'unknown';
      if (!sessionMap?.[sid]) sessionMap[sid] = { session_id: sid, messages: [], first_at: msg?.created_at };
      sessionMap?.[sid]?.messages?.push(msg);
      if (new Date(msg?.created_at) < new Date(sessionMap[sid]?.first_at)) {
        sessionMap[sid].first_at = msg?.created_at;
      }
    }
    // Sort sessions by first message date ASC
    return Object.values(sessionMap)?.sort((a, b) => new Date(a.first_at) - new Date(b.first_at));
  })();

  const currentConvList = getConversationList();
  const currentConv = currentConvList?.find(c => c?.id === selectedId) || null;
  const currentVisitorId = selectedSource === 'web-visitor' ? selectedId : currentConv?.visitor_id;
  const currentVisitor = currentVisitorId ? visitorsMap?.[currentVisitorId] : null;

  // ── Counts ──────────────────────────────────────────────────────────────
  const waOpenCount = waConversations?.filter(c => c?.status === 'open' || c?.status === 'reopened')?.length;
  const waArchivedCount = waConversations?.filter(c => c?.status === 'archived')?.length;
  const webCount = webViewMode === 'visitor' ? webVisitorGroups?.length : webConversations?.length;
  const allCount = waConversations?.length + webCount;

  // ── WA Actions ──────────────────────────────────────────────────────────
  const handleArchive = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
        archived: true, archived_at: new Date()?.toISOString(), archived_by: 'admin'
      })?.eq('phone', phone);
      if (error) throw error;
      if (selectedId === phone) { setSelectedId(null); setSelectedSource(null); setMobileShowMessages(false); }
      await loadWaMessages();
      showToast('Conversation archivée', 'success');
    } catch (err) {
      showToast("Erreur lors de l'archivage", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
        archived: false, archived_at: null, archived_by: null
      })?.eq('phone', phone);
      if (error) throw error;
      await loadWaMessages();
      showToast('Conversation restaurée', 'success');
    } catch (err) {
      showToast('Erreur lors de la restauration', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    const rawPhone = confirmDelete;
    if (!rawPhone) return;
    setConfirmDelete(null);
    setActionLoading(true);
    const cleanPhoneNum = String(rawPhone || '')?.replace(/\D/g, '');

    try {
      const { data: rowsToDelete, error: selectError } = await supabase?.from('wa_messages')?.select('id, phone')?.or(`phone.eq.${cleanPhoneNum},phone.ilike.%${cleanPhoneNum}%`);

      if (selectError) { showToast('Erreur recherche : ' + selectError?.message, 'error'); setActionLoading(false); return; }
      if (!rowsToDelete || rowsToDelete?.length === 0) { showToast('Aucun message trouvé', 'error'); setActionLoading(false); return; }

      const ids = rowsToDelete?.map(row => row?.id);
      const { error: deleteError } = await supabase?.from('wa_messages')?.delete()?.in('id', ids);
      if (deleteError) { showToast('Erreur suppression : ' + deleteError?.message, 'error'); setActionLoading(false); return; }

      setAllWaMessages(prev => prev?.filter(row => !ids?.includes(row?.id)));
      setWaConversations(prev => prev?.filter(conv => conv?.phone !== cleanPhoneNum));
      if (selectedId === cleanPhoneNum) { setSelectedId(null); setSelectedSource(null); setMobileShowMessages(false); }

      await loadWaMessages();
      showToast('Conversation supprimée définitivement', 'success');
    } catch (err) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── WA Send reply ───────────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!replyText?.trim() || !selectedId || selectedSource !== 'whatsapp') return;
    setSending(true);
    setSendError('');
    setSendSuccess('');
    const messageText = replyText?.trim();

    try {
      const { data: fnData, error: fnError } = await supabase?.functions?.invoke('send-whatsapp-admin-message', {
        body: { to: selectedId, message: messageText }
      });
      if (fnError) { setSendError("Erreur lors de l'envoi du message WhatsApp."); setSending(false); return; }
      if (!fnData?.success) { setSendError(`Erreur Meta API: ${fnData?.error || 'Envoi échoué'}`); setSending(false); return; }

      const { data: insertedMsg, error: insertError } = await supabase?.from('wa_messages')?.insert({ phone: selectedId, admin_message: messageText, created_at: new Date()?.toISOString() })?.select()?.single();

      if (!insertError && insertedMsg) {
        setAllWaMessages(prev => { const updated = [insertedMsg, ...prev]; setWaConversations(buildWaConversations(updated)); return updated; });
      }
      await supabase?.from('wa_conversations')?.update({ updated_at: new Date()?.toISOString() })?.eq('phone', selectedId);
      setReplyText('');
      setSendSuccess('Message envoyé avec succès !');
      setTimeout(() => setSendSuccess(''), 3000);
    } catch (err) {
      setSendError("Erreur inattendue lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  // ── Human takeover (WA) ─────────────────────────────────────────────────
  const handleHumanTakeover = async (value) => {
    if (!selectedId || selectedSource !== 'whatsapp') return;
    try {
      await supabase?.from('wa_conversations')?.upsert(
        { phone: selectedId, human_takeover: value, updated_at: new Date()?.toISOString() },
        { onConflict: 'phone' }
      );
      setWaConversations(prev => prev?.map(c => c?.phone === selectedId ? { ...c, human_takeover: value } : c));
    } catch (err) {
      console.error('Takeover error:', err);
    }
  };

  // ── Visitor admin actions ───────────────────────────────────────────────
  const handleVisitorStatusChange = async (visitorDbId, newStatus) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], status: newStatus }
        }));
      }
      showToast('Statut mis à jour', 'success');
    } catch (err) {
      showToast('Erreur mise à jour statut', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisitorToggleTakeover = async (visitorDbId, value) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ human_takeover: value, updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], human_takeover: value }
        }));
      }
      showToast(value ? 'Reprise humaine activée' : 'IA réactivée', 'success');
    } catch (err) {
      showToast('Erreur mise à jour reprise humaine', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisitorArchive = async (visitorDbId) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ is_archived: true, status: 'archived', updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], is_archived: true, status: 'archived' }
        }));
      }
      showToast('Visiteur archivé', 'success');
    } catch (err) {
      showToast('Erreur archivage visiteur', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectConv = (conv) => {
    setSelectedId(conv?.id);
    setSelectedSource(conv?.source);
    setMobileShowMessages(true);
    setSendError('');
    setSendSuccess('');
    setReplyText('');
  };

  const handleRefresh = () => {
    loadWaMessages();
    loadWebLogs();
  };

  // When switching web view mode, clear selection if it's a web selection
  const handleWebViewModeChange = (mode) => {
    setWebViewMode(mode);
    if (selectedSource === 'web' || selectedSource === 'web-visitor') {
      setSelectedId(null);
      setSelectedSource(null);
      setMobileShowMessages(false);
    }
  };

  const isLoading = loadingWa || loadingWeb;
  const isWebSource = selectedSource === 'web' || selectedSource === 'web-visitor';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
      {confirmDelete && (
        <ConfirmDialog
          message="Voulez-vous vraiment supprimer définitivement cette conversation ? Cette action supprimera tous les messages de ce numéro et ne pourra pas être annulée."
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <div className="flex h-[calc(100vh-64px)]">
        {/* ── Left panel ─────────────────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-emerald-600" />
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">Conversations Fatou</h1>
              </div>
              <button
                onClick={handleRefresh}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors"
                title="Actualiser"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Source tabs */}
            <div className="flex gap-1 mb-3">
              {[
                { key: 'whatsapp', label: 'WhatsApp', icon: Smartphone, count: waConversations?.length, color: 'green' },
                { key: 'web', label: 'Site web', icon: Globe, count: webCount, color: 'indigo' },
                { key: 'all', label: 'Toutes', icon: MessageSquare, count: allCount, color: 'gray' },
              ]?.map(tab => {
                const TabIcon = tab?.icon;
                const isActive = sourceTab === tab?.key;
                return (
                  <button
                    key={tab?.key}
                    onClick={() => { setSourceTab(tab?.key); setSelectedId(null); setSelectedSource(null); }}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-1 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ? tab?.color === 'green' ? 'bg-green-600 text-white'
                          : tab?.color === 'indigo'? 'bg-indigo-600 text-white' :'bg-gray-700 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <TabIcon className="h-3 w-3" />
                    <span>{tab?.label}</span>
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                    }`}>{tab?.count}</span>
                  </button>
                );
              })}
            </div>

            {/* Web view mode selector */}
            {(sourceTab === 'web' || sourceTab === 'all') && (
              <div className="flex gap-1 mb-3 p-1 bg-gray-100 dark:bg-slate-700 rounded-lg">
                <button
                  onClick={() => handleWebViewModeChange('visitor')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                    webViewMode === 'visitor' ?'bg-white dark:bg-slate-600 text-indigo-700 dark:text-indigo-300 shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <Users className="h-3 w-3" />
                  Par visiteur
                </button>
                <button
                  onClick={() => handleWebViewModeChange('session')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                    webViewMode === 'session' ?'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-300 shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <List className="h-3 w-3" />
                  Par session
                </button>
              </div>
            )}

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, ville, numéro, barrique, à rappeler..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Status filters */}
            {(sourceTab === 'whatsapp' || sourceTab === 'all') && (
              <div className="flex gap-1">
                {[
                  { key: 'open', label: 'Ouvertes', count: waOpenCount },
                  { key: 'archived', label: 'Archivées', count: waArchivedCount },
                  { key: 'all', label: 'Toutes', count: waConversations?.length },
                ]?.map(f => (
                  <button
                    key={f?.key}
                    onClick={() => setStatusFilter(f?.key)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === f?.key
                        ? 'bg-green-600 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {f?.label}
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                      statusFilter === f?.key ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                    }`}>{f?.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Error display */}
          {(waError || webError) && (
            <div className="mx-3 mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400">Erreur de chargement</p>
                {waError && <p className="text-xs text-red-500 mt-0.5">WA: {waError}</p>}
                {webError && <p className="text-xs text-red-500 mt-0.5">Web: {webError}</p>}
              </div>
            </div>
          )}

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredConversations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">{searchQuery ? 'Aucun résultat' : 'Aucune conversation'}</p>
              </div>
            ) : (
              filteredConversations?.map(conv => {
                const visitor = conv?.visitor_id ? visitorsMap?.[conv?.visitor_id] : null;
                let displayName;
                if (conv?.source === 'web-visitor') {
                  displayName = visitor?.name || `Visiteur ${shortVisitorId(conv?.visitor_id)}`;
                } else if (conv?.source === 'web') {
                  displayName = visitor?.name || `Session ${conv?.sessionShort}`;
                } else {
                  displayName = conv?.displayName;
                }

                const isSelected = selectedId === conv?.id && selectedSource === conv?.source;

                return (
                  <div key={`${conv?.source}-${conv?.id}`} className="relative group">
                    <button
                      onClick={() => handleSelectConv(conv)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${
                        isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Avatar */}
                          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            conv?.source === 'web-visitor' ? 'bg-indigo-500' :
                            conv?.source === 'web' ? 'bg-indigo-400' :
                            conv?.status === 'archived' ? 'bg-gray-400' :
                            conv?.status === 'reopened'? 'bg-orange-500' : conv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                          }`}>
                            {(conv?.source === 'web-visitor' || conv?.source === 'web') ? <Globe className="h-4 w-4" /> :
                             conv?.status === 'archived' ? <Archive className="h-4 w-4" /> :
                             conv?.status === 'reopened' ? <History className="h-4 w-4" /> :
                             conv?.human_takeover ? <Shield className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{displayName}</p>
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && (
                                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded">
                                  <Globe className="h-2 w-2" />Site
                                </span>
                              )}
                              {conv?.source === 'web-visitor' && conv?.session_count > 1 && (
                                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 rounded">
                                  {conv?.session_count} sessions
                                </span>
                              )}
                            </div>
                            {/* Phone / city for web */}
                            {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.phone && (
                              <p className="text-[10px] text-gray-400 truncate">{visitor?.phone}{visitor?.city ? ` · ${visitor?.city}` : ''}</p>
                            )}
                            {conv?.last_message && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{conv?.last_message}</p>
                            )}
                            {/* Badges */}
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {conv?.source === 'whatsapp' && conv?.status === 'reopened' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded">Réouverte</span>
                              )}
                              {conv?.source === 'whatsapp' && conv?.status === 'archived' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">Archivée</span>
                              )}
                              {conv?.source === 'whatsapp' && conv?.human_takeover && conv?.status !== 'archived' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded">Admin</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && conv?.is_flagged && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded">⚑ Signalé</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.status === 'needs_callback' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded">À rappeler</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.human_takeover && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded">Reprise humaine</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.status && visitor?.status !== 'new' && (
                                <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${LEAD_STATUS_LABELS?.[visitor?.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                                  {LEAD_STATUS_LABELS?.[visitor?.status]?.label || visitor?.status}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-xs text-gray-400">{formatDate(conv?.last_message_at)}</p>
                        </div>
                      </div>
                    </button>
                    {/* Action buttons on hover (WA only) */}
                    {conv?.source === 'whatsapp' && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-white dark:bg-slate-800 shadow-md rounded-lg p-1 z-10">
                        {(conv?.status === 'open' || conv?.status === 'reopened') && (
                          <button onClick={(e) => { e?.stopPropagation(); handleArchive(conv?.phone); }} disabled={actionLoading} title="Archiver" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-orange-600 transition-colors disabled:opacity-50">
                            <Archive className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {conv?.status === 'archived' && (
                          <button onClick={(e) => { e?.stopPropagation(); handleRestore(conv?.phone); }} disabled={actionLoading} title="Restaurer" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-50">
                            <ArchiveRestore className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={(e) => { e?.stopPropagation(); setConfirmDelete(conv?.phone); }} disabled={actionLoading} title="Supprimer définitivement" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right panel ─────────────────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-gray-50 dark:bg-slate-900 min-w-0`}>
          {!selectedId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Bot className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">Sélectionnez une conversation</p>
              <p className="text-sm mt-1">Cliquez sur un contact à gauche pour voir ses messages</p>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => setMobileShowMessages(false)} className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 flex-shrink-0">
                      <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white ${
                      selectedSource === 'web-visitor' ? 'bg-indigo-500' :
                      selectedSource === 'web' ? 'bg-indigo-400' :
                      currentConv?.status === 'archived' ? 'bg-gray-400' :
                      currentConv?.status === 'reopened'? 'bg-orange-500' : currentConv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                    }`}>
                      {isWebSource ? <Globe className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {selectedSource === 'web-visitor'
                            ? (currentVisitor?.name || `Visiteur ${shortVisitorId(selectedId)}`)
                            : selectedSource === 'web'
                            ? (currentVisitor?.name || `Visiteur site web — Session ${shortSession(selectedId)}`)
                            : displayPhone(selectedId)}
                        </p>
                        {isWebSource && (
                          <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded">
                            <Globe className="h-2.5 w-2.5" />Site web
                          </span>
                        )}
                        {selectedSource === 'web-visitor' && currentConv?.session_count > 1 && (
                          <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded">
                            {currentConv?.session_count} sessions
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedMessages?.length} message{selectedMessages?.length !== 1 ? 's' : ''}
                        {selectedSource === 'web-visitor' && currentConv?.session_count > 1
                          ? ` · ${currentConv?.session_count} sessions`
                          : ` · Dernier : ${formatFullDate(currentConv?.last_message_at)}`}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                    {selectedSource === 'whatsapp' && (
                      <>
                        {(currentConv?.status === 'open' || currentConv?.status === 'reopened') && (
                          <button onClick={() => handleArchive(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50">
                            <Archive className="h-3.5 w-3.5" />Archiver
                          </button>
                        )}
                        {currentConv?.status === 'archived' && (
                          <button onClick={() => handleRestore(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50">
                            <ArchiveRestore className="h-3.5 w-3.5" />Restaurer
                          </button>
                        )}
                        <button onClick={() => setConfirmDelete(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50">
                          <Trash2 className="h-3.5 w-3.5" />Supprimer
                        </button>
                        {currentConv?.human_takeover ? (
                          <button onClick={() => handleHumanTakeover(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg hover:bg-emerald-200 transition-colors">
                            <Bot className="h-3.5 w-3.5" />Réactiver Fatou
                          </button>
                        ) : (
                          <button onClick={() => handleHumanTakeover(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-200 transition-colors">
                            <Shield className="h-3.5 w-3.5" />Reprise humaine
                          </button>
                        )}
                      </>
                    )}
                    <button onClick={handleRefresh} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors" title="Actualiser">
                      <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Banners */}
                {selectedSource === 'whatsapp' && currentConv?.status === 'reopened' && currentConv?.lastArchivedAt && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <History className="h-4 w-4 text-orange-600 flex-shrink-0" />
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      <strong>Historique client retrouvé</strong> — dernier contact précédent : {formatFullDate(currentConv?.lastArchivedAt)}
                    </p>
                  </div>
                )}
                {selectedSource === 'whatsapp' && currentConv?.human_takeover && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Reprise humaine active</strong> — Fatou IA ne répondra plus automatiquement.</p>
                  </div>
                )}
                {selectedSource === 'whatsapp' && currentConv?.status === 'archived' && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                    <Archive className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <p className="text-xs text-gray-600 dark:text-gray-400"><strong>Conversation archivée</strong> — Restaurez-la pour reprendre les échanges.</p>
                  </div>
                )}
                {isWebSource && currentConv?.is_flagged && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-300"><strong>Conversation signalée</strong> — Un ou plusieurs messages ont été marqués pour révision.</p>
                  </div>
                )}
                {isWebSource && currentVisitor?.human_takeover && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Reprise humaine active</strong> — Fatou IA ne répondra plus automatiquement à ce visiteur.</p>
                  </div>
                )}
              </div>

              {/* Messages + Visitor card area */}
              <div className="flex flex-1 min-h-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {selectedMessages?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                      <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                      <p className="text-sm">Aucun message trouvé</p>
                    </div>
                  ) : selectedSource === 'web-visitor' && selectedMessagesBySession ? (
                    // Visitor view: messages grouped by session with separators
                    (<>
                      {selectedMessagesBySession?.map((sessionGroup, idx) => (
                        <div key={sessionGroup?.session_id}>
                          <SessionSeparator
                            sessionId={sessionGroup?.session_id}
                            firstMessageDate={sessionGroup?.first_at}
                            messageCount={sessionGroup?.messages?.length}
                            index={idx}
                          />
                          {sessionGroup?.messages?.map(msg => (
                            <WebBubble key={msg?.id} msg={msg} />
                          ))}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>)
                  ) : (
                    <>
                      {selectedMessages?.map(msg => (
                        selectedSource === 'web'
                          ? <WebBubble key={msg?.id} msg={msg} />
                          : <WaBubble key={msg?.id} msg={msg} />
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Visitor card sidebar (web only) */}
                {isWebSource && (
                  <div className="w-72 flex-shrink-0 overflow-y-auto p-4 border-l border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    {loadingVisitor ? (
                      <div className="flex items-center justify-center h-20">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : currentVisitor ? (
                      <VisitorCard
                        visitor={currentVisitor}
                        onStatusChange={handleVisitorStatusChange}
                        onToggleTakeover={handleVisitorToggleTakeover}
                        onArchive={handleVisitorArchive}
                        loading={actionLoading}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-xs">Aucune fiche visiteur</p>
                        <p className="text-[10px] mt-1 text-gray-300">La fiche sera créée dès que le visiteur envoie un message avec son visitor_id.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reply area — WA only */}
              {selectedSource === 'whatsapp' && (
                <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex-shrink-0">
                  {sendError && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-600 dark:text-red-400">{sendError}</p>
                    </div>
                  )}
                  {sendSuccess && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <p className="text-xs text-green-600 dark:text-green-400">{sendSuccess}</p>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e?.target?.value)}
                      onKeyDown={e => { if (e?.key === 'Enter' && !e?.shiftKey) { e?.preventDefault(); handleSendReply(); } }}
                      placeholder="Répondre au client... (Entrée pour envoyer, Maj+Entrée pour nouvelle ligne)"
                      rows={2}
                      className="flex-1 resize-none px-3 py-2.5 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-xl text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={sending || !replyText?.trim()}
                      className="flex-shrink-0 p-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                      title="Envoyer"
                    >
                      {sending ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <Send className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Envoi via Meta WhatsApp Cloud API • Numéro ADT : +33651988621</p>
                </div>
              )}

              {/* Web: read-only notice */}
              {isWebSource && (
                <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-3 flex-shrink-0">
                  <p className="text-xs text-gray-400 text-center">
                    <Globe className="h-3.5 w-3.5 inline mr-1" />
                    Conversation site web — lecture seule. La réponse est gérée automatiquement par Fatou IA.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function FatouConversationsContent({ containerHeight = 'calc(100vh - 180px)' }) {
  const [sourceTab, setSourceTab] = useState('whatsapp');
  // 'visitor' = grouped by visitor_id (default), 'session' = one row per session
  const [webViewMode, setWebViewMode] = useState('visitor');

  // WhatsApp state
  const [allWaMessages, setAllWaMessages] = useState([]);
  const [waConversations, setWaConversations] = useState([]);
  const [loadingWa, setLoadingWa] = useState(true);
  const [waError, setWaError] = useState(null);

  // Web state
  const [webLogs, setWebLogs] = useState([]);
  const [webConversations, setWebConversations] = useState([]); // per-session
  const [webVisitorGroups, setWebVisitorGroups] = useState([]); // per-visitor
  const [loadingWeb, setLoadingWeb] = useState(true);
  const [webError, setWebError] = useState(null);

  // Visitor state
  const [visitorsMap, setVisitorsMap] = useState({}); // keyed by visitor_id
  const [loadingVisitor, setLoadingVisitor] = useState(false);

  // Selection
  // For visitor mode: selectedId = visitor_id, selectedSource = 'web-visitor'
  // For session mode: selectedId = session_id, selectedSource = 'web'
  // For WA: selectedId = phone, selectedSource = 'whatsapp'
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  // UI
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileShowMessages, setMobileShowMessages] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // WA reply
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const messagesEndRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  // ── Build WA conversations ──────────────────────────────────────────────
  const buildWaConversations = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      const phone = String(row?.phone || '')?.replace(/\D/g, '');
      if (!phone) continue;
      if (!grouped?.[phone]) grouped[phone] = { phone, rows: [] };
      grouped?.[phone]?.rows?.push(row);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || latest?.assistant_response || latest?.admin_message || 'Message vide';
      const convStatus = getConversationStatus(g?.rows);
      const archivedRows = g?.rows?.filter(r => r?.archived === true && r?.archived_at);
      const lastArchivedAt = archivedRows?.length > 0
        ? archivedRows?.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at))?.[0]?.archived_at
        : null;

      return {
        source: 'whatsapp',
        id: g?.phone,
        phone: g?.phone,
        displayName: displayPhone(g?.phone),
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: convStatus,
        lastArchivedAt,
        messages: g?.rows,
        human_takeover: false,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Build Web conversations (per session) ───────────────────────────────
  const buildWebConversations = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      const sid = row?.session_id;
      if (!sid) continue;
      if (!grouped?.[sid]) grouped[sid] = { session_id: sid, rows: [] };
      grouped?.[sid]?.rows?.push(row);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || 'Message vide';
      const hasFlagged = g?.rows?.some(r => r?.is_flagged);
      const visitorId = latest?.visitor_id || null;
      const leadStatus = latest?.lead_status || null;
      const humanTakeover = g?.rows?.some(r => r?.human_takeover);

      return {
        source: 'web',
        id: g?.session_id,
        session_id: g?.session_id,
        visitor_id: visitorId,
        displayName: `Visiteur site web`,
        sessionShort: shortSession(g?.session_id),
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: 'open',
        lead_status: leadStatus,
        is_flagged: hasFlagged,
        human_takeover: humanTakeover,
        messages: g?.rows,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Build Web visitor groups (per visitor_id) ───────────────────────────
  const buildWebVisitorGroups = useCallback((data) => {
    const grouped = {};
    for (const row of (data || [])) {
      // Use visitor_id if available, fallback to visitor_ref_id, then session_id
      const vid = row?.visitor_id || row?.visitor_ref_id || row?.session_id;
      if (!vid) continue;
      if (!grouped?.[vid]) grouped[vid] = { visitor_id: vid, rows: [], sessions: new Set() };
      grouped?.[vid]?.rows?.push(row);
      if (row?.session_id) grouped?.[vid]?.sessions?.add(row?.session_id);
    }

    return Object.values(grouped)?.map(g => {
      const sortedRows = [...g?.rows]?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = sortedRows?.[0];
      const lastMsg = latest?.user_message || 'Message vide';
      const hasFlagged = g?.rows?.some(r => r?.is_flagged);
      const humanTakeover = g?.rows?.some(r => r?.human_takeover);
      const leadStatus = latest?.lead_status || null;
      const sessionCount = g?.sessions?.size;

      return {
        source: 'web-visitor',
        id: g?.visitor_id,
        visitor_id: g?.visitor_id,
        session_ids: Array.from(g?.sessions),
        session_count: sessionCount,
        displayName: `Visiteur site web`,
        last_message: lastMsg,
        last_message_at: latest?.created_at,
        status: 'open',
        lead_status: leadStatus,
        is_flagged: hasFlagged,
        human_takeover: humanTakeover,
        messages: g?.rows,
      };
    })?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
  }, []);

  // ── Load WhatsApp messages ──────────────────────────────────────────────
  const loadWaMessages = useCallback(async () => {
    setLoadingWa(true);
    setWaError(null);
    try {
      const { data, error } = await supabase?.from('wa_messages')?.select('id, phone, user_message, assistant_response, admin_message, created_at, order_confirmed, archived, archived_at, archived_by')?.order('created_at', { ascending: false })?.limit(1000);

      if (error) { setWaError(error?.message); setLoadingWa(false); return; }

      const convList = buildWaConversations(data);

      if (convList?.length > 0) {
        const phones = convList?.map(c => c?.phone);
        const { data: convData } = await supabase?.from('wa_conversations')?.select('phone, human_takeover')?.in('phone', phones);
        if (convData?.length > 0) {
          const convMap = {};
          for (const c of convData) convMap[String(c.phone || '').replace(/\D/g, '')] = c;
          for (const g of convList) {
            const match = convMap?.[g?.phone];
            if (match) g.human_takeover = match?.human_takeover ?? false;
          }
        }
      }

      setAllWaMessages(data || []);
      setWaConversations(convList);
    } catch (err) {
      setWaError(err?.message || 'Erreur inattendue');
    } finally {
      setLoadingWa(false);
    }
  }, [buildWaConversations]);

  // ── Load Web logs ───────────────────────────────────────────────────────
  const loadWebLogs = useCallback(async () => {
    setLoadingWeb(true);
    setWebError(null);
    try {
      const { data, error } = await supabase?.from('fatou_conversation_logs')
        ?.select('id, session_id, visitor_id, visitor_ref_id, user_message, ai_response, confidence_score, is_flagged, flag_reason, conversation_topic, response_time_ms, lead_status, human_takeover, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(1000);

      if (error) { setWebError(error?.message); setLoadingWeb(false); return; }

      setWebLogs(data || []);
      setWebConversations(buildWebConversations(data));
      setWebVisitorGroups(buildWebVisitorGroups(data));
    } catch (err) {
      setWebError(err?.message || 'Erreur inattendue');
    } finally {
      setLoadingWeb(false);
    }
  }, [buildWebConversations, buildWebVisitorGroups]);

  // ── Load visitor for a web conversation ────────────────────────────────
  const loadVisitorForConv = useCallback(async (visitorId) => {
    if (!visitorId || visitorsMap?.[visitorId]) return;
    setLoadingVisitor(true);
    try {
      const { data } = await supabase?.from('fatou_web_visitors')
        ?.select('*')
        ?.eq('visitor_id', visitorId)
        ?.maybeSingle();
      if (data) {
        setVisitorsMap(prev => ({ ...prev, [visitorId]: data }));
      }
    } catch (err) {
      console.error('loadVisitorForConv error:', err);
    } finally {
      setLoadingVisitor(false);
    }
  }, [visitorsMap]);

  useEffect(() => {
    loadWaMessages();
    loadWebLogs();
  }, [loadWaMessages, loadWebLogs]);

  // ── Real-time WA ────────────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase?.channel('fatou_conv_wa_rt')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'wa_messages' }, (payload) => {
        const newMsg = payload?.new;
        setAllWaMessages(prev => {
          const updated = [newMsg, ...prev];
          setWaConversations(buildWaConversations(updated));
          return updated;
        });
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [buildWaConversations]);

  // ── Real-time Web ───────────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase?.channel('fatou_conv_web_rt')?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'fatou_conversation_logs' }, (payload) => {
        const newLog = payload?.new;
        setWebLogs(prev => {
          const updated = [newLog, ...prev];
          setWebConversations(buildWebConversations(updated));
          setWebVisitorGroups(buildWebVisitorGroups(updated));
          return updated;
        });
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [buildWebConversations, buildWebVisitorGroups]);

  // ── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  });

  // ── Load visitor when web conv selected ────────────────────────────────
  useEffect(() => {
    if (selectedSource === 'web' && selectedId) {
      const conv = webConversations?.find(c => c?.id === selectedId);
      if (conv?.visitor_id) {
        loadVisitorForConv(conv?.visitor_id);
      }
    }
    if (selectedSource === 'web-visitor' && selectedId) {
      loadVisitorForConv(selectedId);
    }
  }, [selectedId, selectedSource, webConversations, loadVisitorForConv]);

  // ── Compute unified conversation list ───────────────────────────────────
  const allConversations = [
    ...waConversations?.map(c => ({ ...c, source: 'whatsapp' })),
    ...(webViewMode === 'visitor' ? webVisitorGroups : webConversations)?.map(c => ({ ...c })),
  ]?.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));

  const getConversationList = () => {
    if (sourceTab === 'whatsapp') return waConversations;
    if (sourceTab === 'web') return webViewMode === 'visitor' ? webVisitorGroups : webConversations;
    return allConversations;
  };

  const filteredConversations = getConversationList()?.filter(conv => {
    if (conv?.source === 'whatsapp') {
      if (statusFilter === 'open' && conv?.status !== 'open' && conv?.status !== 'reopened') return false;
      if (statusFilter === 'archived' && conv?.status !== 'archived') return false;
    } else {
      if (statusFilter === 'archived') return false;
    }

    if (!searchQuery) return true;

    const search = normalizeText(searchQuery);
    const cleanSearchPhone = searchQuery?.replace(/\D/g, '');

    // Also search in visitor data
    const visitorId = conv?.visitor_id;
    const visitor = visitorId ? visitorsMap?.[visitorId] : null;
    if (visitor) {
      const visitorText = [visitor?.name, visitor?.phone, visitor?.email, visitor?.city, visitor?.postal_code, visitor?.last_summary, visitor?.last_intent, visitor?.status]?.filter(Boolean)?.join(' ');
      if (normalizeText(visitorText)?.includes(search)) return true;
      if (cleanSearchPhone && visitor?.phone?.replace(/\D/g, '')?.includes(cleanSearchPhone)) return true;
    }

    return conv?.messages?.some(row => {
      if (conv?.source === 'whatsapp') {
        const phone = String(row?.phone || '');
        const cleanRowPhone = phone?.replace(/\D/g, '');
        const text = [row?.phone, row?.user_message, row?.assistant_response, row?.admin_message, row?.created_at]?.filter(Boolean)?.join(' ');
        const normalizedText = normalizeText(text);
        return normalizedText?.includes(search) || (cleanSearchPhone && cleanRowPhone?.includes(cleanSearchPhone));
      } else {
        const text = [row?.session_id, row?.visitor_id, row?.user_message, row?.ai_response, row?.conversation_topic, row?.lead_status, row?.created_at]?.filter(Boolean)?.join(' ');
        const normalizedText = normalizeText(text);
        return normalizedText?.includes(search) || (cleanSearchPhone && String(row?.session_id || '')?.includes(cleanSearchPhone));
      }
    });
  });

  // ── Selected conversation messages ──────────────────────────────────────
  const selectedMessages = (() => {
    if (!selectedId || !selectedSource) return [];
    if (selectedSource === 'whatsapp') {
      return [...allWaMessages?.filter(row => String(row?.phone || '')?.replace(/\D/g, '') === selectedId)]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (selectedSource === 'web-visitor') {
      // All messages for this visitor across all sessions, sorted ASC
      return [...webLogs?.filter(row => {
        const vid = row?.visitor_id || row?.visitor_ref_id || row?.session_id;
        return vid === selectedId;
      })]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else {
      return [...webLogs?.filter(row => row?.session_id === selectedId)]?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
  })();

  // ── Group selected messages by session (for visitor view) ───────────────
  const selectedMessagesBySession = (() => {
    if (selectedSource !== 'web-visitor') return null;
    const sessionMap = {};
    for (const msg of selectedMessages) {
      const sid = msg?.session_id || 'unknown';
      if (!sessionMap?.[sid]) sessionMap[sid] = { session_id: sid, messages: [], first_at: msg?.created_at };
      sessionMap?.[sid]?.messages?.push(msg);
      if (new Date(msg?.created_at) < new Date(sessionMap[sid]?.first_at)) {
        sessionMap[sid].first_at = msg?.created_at;
      }
    }
    // Sort sessions by first message date ASC
    return Object.values(sessionMap)?.sort((a, b) => new Date(a.first_at) - new Date(b.first_at));
  })();

  const currentConvList = getConversationList();
  const currentConv = currentConvList?.find(c => c?.id === selectedId) || null;
  const currentVisitorId = selectedSource === 'web-visitor' ? selectedId : currentConv?.visitor_id;
  const currentVisitor = currentVisitorId ? visitorsMap?.[currentVisitorId] : null;

  // ── Counts ──────────────────────────────────────────────────────────────
  const waOpenCount = waConversations?.filter(c => c?.status === 'open' || c?.status === 'reopened')?.length;
  const waArchivedCount = waConversations?.filter(c => c?.status === 'archived')?.length;
  const webCount = webViewMode === 'visitor' ? webVisitorGroups?.length : webConversations?.length;
  const allCount = waConversations?.length + webCount;

  // ── WA Actions ──────────────────────────────────────────────────────────
  const handleArchive = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
        archived: true, archived_at: new Date()?.toISOString(), archived_by: 'admin'
      })?.eq('phone', phone);
      if (error) throw error;
      if (selectedId === phone) { setSelectedId(null); setSelectedSource(null); setMobileShowMessages(false); }
      await loadWaMessages();
      showToast('Conversation archivée', 'success');
    } catch (err) {
      showToast("Erreur lors de l'archivage", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (phone) => {
    if (!phone) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('wa_messages')?.update({
        archived: false, archived_at: null, archived_by: null
      })?.eq('phone', phone);
      if (error) throw error;
      await loadWaMessages();
      showToast('Conversation restaurée', 'success');
    } catch (err) {
      showToast('Erreur lors de la restauration', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    const rawPhone = confirmDelete;
    if (!rawPhone) return;
    setConfirmDelete(null);
    setActionLoading(true);
    const cleanPhoneNum = String(rawPhone || '')?.replace(/\D/g, '');

    try {
      const { data: rowsToDelete, error: selectError } = await supabase?.from('wa_messages')?.select('id, phone')?.or(`phone.eq.${cleanPhoneNum},phone.ilike.%${cleanPhoneNum}%`);

      if (selectError) { showToast('Erreur recherche : ' + selectError?.message, 'error'); setActionLoading(false); return; }
      if (!rowsToDelete || rowsToDelete?.length === 0) { showToast('Aucun message trouvé', 'error'); setActionLoading(false); return; }

      const ids = rowsToDelete?.map(row => row?.id);
      const { error: deleteError } = await supabase?.from('wa_messages')?.delete()?.in('id', ids);
      if (deleteError) { showToast('Erreur suppression : ' + deleteError?.message, 'error'); setActionLoading(false); return; }

      setAllWaMessages(prev => prev?.filter(row => !ids?.includes(row?.id)));
      setWaConversations(prev => prev?.filter(conv => conv?.phone !== cleanPhoneNum));
      if (selectedId === cleanPhoneNum) { setSelectedId(null); setSelectedSource(null); setMobileShowMessages(false); }

      await loadWaMessages();
      showToast('Conversation supprimée définitivement', 'success');
    } catch (err) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── WA Send reply ───────────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!replyText?.trim() || !selectedId || selectedSource !== 'whatsapp') return;
    setSending(true);
    setSendError('');
    setSendSuccess('');
    const messageText = replyText?.trim();

    try {
      const { data: fnData, error: fnError } = await supabase?.functions?.invoke('send-whatsapp-admin-message', {
        body: { to: selectedId, message: messageText }
      });
      if (fnError) { setSendError("Erreur lors de l'envoi du message WhatsApp."); setSending(false); return; }
      if (!fnData?.success) { setSendError(`Erreur Meta API: ${fnData?.error || 'Envoi échoué'}`); setSending(false); return; }

      const { data: insertedMsg, error: insertError } = await supabase?.from('wa_messages')?.insert({ phone: selectedId, admin_message: messageText, created_at: new Date()?.toISOString() })?.select()?.single();

      if (!insertError && insertedMsg) {
        setAllWaMessages(prev => { const updated = [insertedMsg, ...prev]; setWaConversations(buildWaConversations(updated)); return updated; });
      }
      await supabase?.from('wa_conversations')?.update({ updated_at: new Date()?.toISOString() })?.eq('phone', selectedId);
      setReplyText('');
      setSendSuccess('Message envoyé avec succès !');
      setTimeout(() => setSendSuccess(''), 3000);
    } catch (err) {
      setSendError("Erreur inattendue lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  // ── Human takeover (WA) ─────────────────────────────────────────────────
  const handleHumanTakeover = async (value) => {
    if (!selectedId || selectedSource !== 'whatsapp') return;
    try {
      await supabase?.from('wa_conversations')?.upsert(
        { phone: selectedId, human_takeover: value, updated_at: new Date()?.toISOString() },
        { onConflict: 'phone' }
      );
      setWaConversations(prev => prev?.map(c => c?.phone === selectedId ? { ...c, human_takeover: value } : c));
    } catch (err) {
      console.error('Takeover error:', err);
    }
  };

  // ── Visitor admin actions ───────────────────────────────────────────────
  const handleVisitorStatusChange = async (visitorDbId, newStatus) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], status: newStatus }
        }));
      }
      showToast('Statut mis à jour', 'success');
    } catch (err) {
      showToast('Erreur mise à jour statut', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisitorToggleTakeover = async (visitorDbId, value) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ human_takeover: value, updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], human_takeover: value }
        }));
      }
      showToast(value ? 'Reprise humaine activée' : 'IA réactivée', 'success');
    } catch (err) {
      showToast('Erreur mise à jour reprise humaine', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVisitorArchive = async (visitorDbId) => {
    if (!visitorDbId) return;
    setActionLoading(true);
    try {
      const { error } = await supabase?.from('fatou_web_visitors')?.update({ is_archived: true, status: 'archived', updated_at: new Date()?.toISOString() })?.eq('id', visitorDbId);
      if (error) throw error;
      if (currentVisitorId) {
        setVisitorsMap(prev => ({
          ...prev,
          [currentVisitorId]: { ...prev?.[currentVisitorId], is_archived: true, status: 'archived' }
        }));
      }
      showToast('Visiteur archivé', 'success');
    } catch (err) {
      showToast('Erreur archivage visiteur', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectConv = (conv) => {
    setSelectedId(conv?.id);
    setSelectedSource(conv?.source);
    setMobileShowMessages(true);
    setSendError('');
    setSendSuccess('');
    setReplyText('');
  };

  const handleRefresh = () => {
    loadWaMessages();
    loadWebLogs();
  };

  // When switching web view mode, clear selection if it's a web selection
  const handleWebViewModeChange = (mode) => {
    setWebViewMode(mode);
    if (selectedSource === 'web' || selectedSource === 'web-visitor') {
      setSelectedId(null);
      setSelectedSource(null);
      setMobileShowMessages(false);
    }
  };

  const isLoading = loadingWa || loadingWeb;
  const isWebSource = selectedSource === 'web' || selectedSource === 'web-visitor';

  return (
    <div className="bg-gray-50 dark:bg-slate-900">
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
      {confirmDelete && (
        <ConfirmDialog
          message="Voulez-vous vraiment supprimer définitivement cette conversation ? Cette action supprimera tous les messages de ce numéro et ne pourra pas être annulée."
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <div className="flex" style={{ height: containerHeight }}>
        {/* ── Left panel ─────────────────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-emerald-600" />
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">Conversations Fatou</h1>
              </div>
              <button
                onClick={handleRefresh}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors"
                title="Actualiser"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Source tabs */}
            <div className="flex gap-1 mb-3">
              {[
                { key: 'whatsapp', label: 'WhatsApp', icon: Smartphone, count: waConversations?.length, color: 'green' },
                { key: 'web', label: 'Site web', icon: Globe, count: webCount, color: 'indigo' },
                { key: 'all', label: 'Toutes', icon: MessageSquare, count: allCount, color: 'gray' },
              ]?.map(tab => {
                const TabIcon = tab?.icon;
                const isActive = sourceTab === tab?.key;
                return (
                  <button
                    key={tab?.key}
                    onClick={() => { setSourceTab(tab?.key); setSelectedId(null); setSelectedSource(null); }}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-1 rounded-lg text-xs font-medium transition-colors ${
                      isActive
                        ? tab?.color === 'green' ? 'bg-green-600 text-white'
                          : tab?.color === 'indigo'? 'bg-indigo-600 text-white' :'bg-gray-700 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <TabIcon className="h-3 w-3" />
                    <span>{tab?.label}</span>
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                    }`}>{tab?.count}</span>
                  </button>
                );
              })}
            </div>

            {/* Web view mode selector */}
            {(sourceTab === 'web' || sourceTab === 'all') && (
              <div className="flex gap-1 mb-3 p-1 bg-gray-100 dark:bg-slate-700 rounded-lg">
                <button
                  onClick={() => handleWebViewModeChange('visitor')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                    webViewMode === 'visitor' ?'bg-white dark:bg-slate-600 text-indigo-700 dark:text-indigo-300 shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <Users className="h-3 w-3" />
                  Par visiteur
                </button>
                <button
                  onClick={() => handleWebViewModeChange('session')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
                    webViewMode === 'session' ?'bg-white dark:bg-slate-600 text-gray-700 dark:text-gray-300 shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <List className="h-3 w-3" />
                  Par session
                </button>
              </div>
            )}

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, ville, numéro, barrique, à rappeler..."
                value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Status filters */}
            {(sourceTab === 'whatsapp' || sourceTab === 'all') && (
              <div className="flex gap-1">
                {[
                  { key: 'open', label: 'Ouvertes', count: waOpenCount },
                  { key: 'archived', label: 'Archivées', count: waArchivedCount },
                  { key: 'all', label: 'Toutes', count: waConversations?.length },
                ]?.map(f => (
                  <button
                    key={f?.key}
                    onClick={() => setStatusFilter(f?.key)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === f?.key
                        ? 'bg-green-600 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {f?.label}
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                      statusFilter === f?.key ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                    }`}>{f?.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Error display */}
          {(waError || webError) && (
            <div className="mx-3 mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400">Erreur de chargement</p>
                {waError && <p className="text-xs text-red-500 mt-0.5">WA: {waError}</p>}
                {webError && <p className="text-xs text-red-500 mt-0.5">Web: {webError}</p>}
              </div>
            </div>
          )}

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredConversations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">{searchQuery ? 'Aucun résultat' : 'Aucune conversation'}</p>
              </div>
            ) : (
              filteredConversations?.map(conv => {
                const visitor = conv?.visitor_id ? visitorsMap?.[conv?.visitor_id] : null;
                let displayName;
                if (conv?.source === 'web-visitor') {
                  displayName = visitor?.name || `Visiteur ${shortVisitorId(conv?.visitor_id)}`;
                } else if (conv?.source === 'web') {
                  displayName = visitor?.name || `Session ${conv?.sessionShort}`;
                } else {
                  displayName = conv?.displayName;
                }

                const isSelected = selectedId === conv?.id && selectedSource === conv?.source;

                return (
                  <div key={`${conv?.source}-${conv?.id}`} className="relative group">
                    <button
                      onClick={() => handleSelectConv(conv)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${
                        isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Avatar */}
                          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            conv?.source === 'web-visitor' ? 'bg-indigo-500' :
                            conv?.source === 'web' ? 'bg-indigo-400' :
                            conv?.status === 'archived' ? 'bg-gray-400' :
                            conv?.status === 'reopened'? 'bg-orange-500' : conv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                          }`}>
                            {(conv?.source === 'web-visitor' || conv?.source === 'web') ? <Globe className="h-4 w-4" /> :
                             conv?.status === 'archived' ? <Archive className="h-4 w-4" /> :
                             conv?.status === 'reopened' ? <History className="h-4 w-4" /> :
                             conv?.human_takeover ? <Shield className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{displayName}</p>
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && (
                                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded">
                                  <Globe className="h-2 w-2" />Site
                                </span>
                              )}
                              {conv?.source === 'web-visitor' && conv?.session_count > 1 && (
                                <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 rounded">
                                  {conv?.session_count} sessions
                                </span>
                              )}
                            </div>
                            {/* Phone / city for web */}
                            {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.phone && (
                              <p className="text-[10px] text-gray-400 truncate">{visitor?.phone}{visitor?.city ? ` · ${visitor?.city}` : ''}</p>
                            )}
                            {conv?.last_message && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{conv?.last_message}</p>
                            )}
                            {/* Badges */}
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {conv?.source === 'whatsapp' && conv?.status === 'reopened' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded">Réouverte</span>
                              )}
                              {conv?.source === 'whatsapp' && conv?.status === 'archived' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">Archivée</span>
                              )}
                              {conv?.source === 'whatsapp' && conv?.human_takeover && conv?.status !== 'archived' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded">Admin</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && conv?.is_flagged && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded">⚑ Signalé</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.status === 'needs_callback' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded">À rappeler</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.human_takeover && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded">Reprise humaine</span>
                              )}
                              {(conv?.source === 'web-visitor' || conv?.source === 'web') && visitor?.status && visitor?.status !== 'new' && (
                                <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${LEAD_STATUS_LABELS?.[visitor?.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                                  {LEAD_STATUS_LABELS?.[visitor?.status]?.label || visitor?.status}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-xs text-gray-400">{formatDate(conv?.last_message_at)}</p>
                        </div>
                      </div>
                    </button>
                    {/* Action buttons on hover (WA only) */}
                    {conv?.source === 'whatsapp' && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-white dark:bg-slate-800 shadow-md rounded-lg p-1 z-10">
                        {(conv?.status === 'open' || conv?.status === 'reopened') && (
                          <button onClick={(e) => { e?.stopPropagation(); handleArchive(conv?.phone); }} disabled={actionLoading} title="Archiver" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-orange-600 transition-colors disabled:opacity-50">
                            <Archive className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {conv?.status === 'archived' && (
                          <button onClick={(e) => { e?.stopPropagation(); handleRestore(conv?.phone); }} disabled={actionLoading} title="Restaurer" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-green-600 transition-colors disabled:opacity-50">
                            <ArchiveRestore className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={(e) => { e?.stopPropagation(); setConfirmDelete(conv?.phone); }} disabled={actionLoading} title="Supprimer définitivement" className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right panel ─────────────────────────────────────────────────── */}
        <div className={`${mobileShowMessages ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-gray-50 dark:bg-slate-900 min-w-0`}>
          {!selectedId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Bot className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg font-medium">Sélectionnez une conversation</p>
              <p className="text-sm mt-1">Cliquez sur un contact à gauche pour voir ses messages</p>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => setMobileShowMessages(false)} className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 flex-shrink-0">
                      <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white ${
                      selectedSource === 'web-visitor' ? 'bg-indigo-500' :
                      selectedSource === 'web' ? 'bg-indigo-400' :
                      currentConv?.status === 'archived' ? 'bg-gray-400' :
                      currentConv?.status === 'reopened'? 'bg-orange-500' : currentConv?.human_takeover ?'bg-blue-500' : 'bg-green-500'
                    }`}>
                      {isWebSource ? <Globe className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">
                          {selectedSource === 'web-visitor'
                            ? (currentVisitor?.name || `Visiteur ${shortVisitorId(selectedId)}`)
                            : selectedSource === 'web'
                            ? (currentVisitor?.name || `Visiteur site web — Session ${shortSession(selectedId)}`)
                            : displayPhone(selectedId)}
                        </p>
                        {isWebSource && (
                          <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded">
                            <Globe className="h-2.5 w-2.5" />Site web
                          </span>
                        )}
                        {selectedSource === 'web-visitor' && currentConv?.session_count > 1 && (
                          <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded">
                            {currentConv?.session_count} sessions
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedMessages?.length} message{selectedMessages?.length !== 1 ? 's' : ''}
                        {selectedSource === 'web-visitor' && currentConv?.session_count > 1
                          ? ` · ${currentConv?.session_count} sessions`
                          : ` · Dernier : ${formatFullDate(currentConv?.last_message_at)}`}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                    {selectedSource === 'whatsapp' && (
                      <>
                        {(currentConv?.status === 'open' || currentConv?.status === 'reopened') && (
                          <button onClick={() => handleArchive(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50">
                            <Archive className="h-3.5 w-3.5" />Archiver
                          </button>
                        )}
                        {currentConv?.status === 'archived' && (
                          <button onClick={() => handleRestore(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50">
                            <ArchiveRestore className="h-3.5 w-3.5" />Restaurer
                          </button>
                        )}
                        <button onClick={() => setConfirmDelete(selectedId)} disabled={actionLoading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50">
                          <Trash2 className="h-3.5 w-3.5" />Supprimer
                        </button>
                        {currentConv?.human_takeover ? (
                          <button onClick={() => handleHumanTakeover(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg hover:bg-emerald-200 transition-colors">
                            <Bot className="h-3.5 w-3.5" />Réactiver Fatou
                          </button>
                        ) : (
                          <button onClick={() => handleHumanTakeover(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-200 transition-colors">
                            <Shield className="h-3.5 w-3.5" />Reprise humaine
                          </button>
                        )}
                      </>
                    )}
                    <button onClick={handleRefresh} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors" title="Actualiser">
                      <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Banners */}
                {selectedSource === 'whatsapp' && currentConv?.status === 'reopened' && currentConv?.lastArchivedAt && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <History className="h-4 w-4 text-orange-600 flex-shrink-0" />
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      <strong>Historique client retrouvé</strong> — dernier contact précédent : {formatFullDate(currentConv?.lastArchivedAt)}
                    </p>
                  </div>
                )}
                {selectedSource === 'whatsapp' && currentConv?.human_takeover && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Reprise humaine active</strong> — Fatou IA ne répondra plus automatiquement.</p>
                  </div>
                )}
                {selectedSource === 'whatsapp' && currentConv?.status === 'archived' && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/20 rounded-lg border border-gray-200 dark:border-gray-700">
                    <Archive className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <p className="text-xs text-gray-600 dark:text-gray-400"><strong>Conversation archivée</strong> — Restaurez-la pour reprendre les échanges.</p>
                  </div>
                )}
                {isWebSource && currentConv?.is_flagged && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    <p className="text-xs text-red-700 dark:text-red-300"><strong>Conversation signalée</strong> — Un ou plusieurs messages ont été marqués pour révision.</p>
                  </div>
                )}
                {isWebSource && currentVisitor?.human_takeover && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Reprise humaine active</strong> — Fatou IA ne répondra plus automatiquement à ce visiteur.</p>
                  </div>
                )}
              </div>

              {/* Messages + Visitor card area */}
              <div className="flex flex-1 min-h-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {selectedMessages?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                      <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                      <p className="text-sm">Aucun message trouvé</p>
                    </div>
                  ) : selectedSource === 'web-visitor' && selectedMessagesBySession ? (
                    // Visitor view: messages grouped by session with separators
                    (<>
                      {selectedMessagesBySession?.map((sessionGroup, idx) => (
                        <div key={sessionGroup?.session_id}>
                          <SessionSeparator
                            sessionId={sessionGroup?.session_id}
                            firstMessageDate={sessionGroup?.first_at}
                            messageCount={sessionGroup?.messages?.length}
                            index={idx}
                          />
                          {sessionGroup?.messages?.map(msg => (
                            <WebBubble key={msg?.id} msg={msg} />
                          ))}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>)
                  ) : (
                    <>
                      {selectedMessages?.map(msg => (
                        selectedSource === 'web'
                          ? <WebBubble key={msg?.id} msg={msg} />
                          : <WaBubble key={msg?.id} msg={msg} />
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Visitor card sidebar (web only) */}
                {isWebSource && (
                  <div className="w-72 flex-shrink-0 overflow-y-auto p-4 border-l border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    {loadingVisitor ? (
                      <div className="flex items-center justify-center h-20">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : currentVisitor ? (
                      <VisitorCard
                        visitor={currentVisitor}
                        onStatusChange={handleVisitorStatusChange}
                        onToggleTakeover={handleVisitorToggleTakeover}
                        onArchive={handleVisitorArchive}
                        loading={actionLoading}
                      />
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <UserCheck className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-xs">Aucune fiche visiteur</p>
                        <p className="text-[10px] mt-1 text-gray-300">La fiche sera créée dès que le visiteur envoie un message avec son visitor_id.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reply area — WA only */}
              {selectedSource === 'whatsapp' && (
                <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex-shrink-0">
                  {sendError && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-600 dark:text-red-400">{sendError}</p>
                    </div>
                  )}
                  {sendSuccess && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <p className="text-xs text-green-600 dark:text-green-400">{sendSuccess}</p>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e?.target?.value)}
                      onKeyDown={e => { if (e?.key === 'Enter' && !e?.shiftKey) { e?.preventDefault(); handleSendReply(); } }}
                      placeholder="Répondre au client... (Entrée pour envoyer, Maj+Entrée pour nouvelle ligne)"
                      rows={2}
                      className="flex-1 resize-none px-3 py-2.5 text-sm bg-gray-100 dark:bg-slate-700 border-0 rounded-xl text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={sending || !replyText?.trim()}
                      className="flex-shrink-0 p-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                      title="Envoyer"
                    >
                      {sending ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <Send className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Envoi via Meta WhatsApp Cloud API • Numéro ADT : +33651988621</p>
                </div>
              )}

              {/* Web: read-only notice */}
              {isWebSource && (
                <div className="bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-4 py-3 flex-shrink-0">
                  <p className="text-xs text-gray-400 text-center">
                    <Globe className="h-3.5 w-3.5 inline mr-1" />
                    Conversation site web — lecture seule. La réponse est gérée automatiquement par Fatou IA.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
